import { speciaBehiver, specialCheck, specialCheckCurrent } from '@/define/limit'
import { Http } from '@/network/http'
import { DeviceManager } from '@/service/device'
import { DriverService } from '@/service/driver/driver'
import { ref } from 'vue'
import { compare } from 'compare-versions'
import { Brand, getBrand } from '@/brand/brand'
import { delay } from '@libs/utils/tools'
import i18n from '@/languages/i18n'
import { DialogType, enableDialog } from '@/components/cdialog/dialog'
import { DevicePid } from '@/define/define'
import { JingTaiMer68Devices, JingTaiMg75Devices, RoYuanDeviceList_0005, RoYuanMer68Devices } from '@/define/device_vendor'

interface UpdateInfo {
  VersionName: string
  VersionCode: string
  DownloadURL: string
  HashCode: string
  DeviceIndex: number
  IsForced: boolean
  IsDebug: boolean
  DownloadURLWithGUI: string
  IsTransVer: boolean
  TransVerLang: string
  LayoutVersion: number
  Desc: string
}

class Firmware {
  private updates: UpdateInfo[] = []
  isUpgrade = ref(false)
  firmwareVersion = ref<string | null>(null)
  get upgradeUrl(): string {
    const pid = DeviceManager.instance.state.currentPid
    const res = this.updates.filter((e) => e.DeviceIndex == pid)
    if (res.length == 0) {
      return ''
    }
    const updateInfo = res[0]
    return updateInfo.DownloadURLWithGUI
  }

  fwVersionDvs1: DevicePid[] = [DevicePid.IROKNa87Pro, DevicePid.IROKNd63, DevicePid.IROKNd63_cyan, DevicePid.IYXMu68Pro, DevicePid.IYXMu68Pro_cyan]

  fwVersionDvs2: DevicePid[] = [
    DevicePid.IYXMu68_ULTRA, // 1.0.0
  ]

  private _firmwareVersion: null | string = null
  private resolvesOfGetFirmwareVersion: ((value: string) => void)[] = []

  get cachedFirmwareVersion() {
    return this._firmwareVersion
  }

  async getFirmwareVersion(): Promise<string> {
    if (this.firmwareVersion.value != null) return this.firmwareVersion.value
    if (this._firmwareVersion != null) return this._firmwareVersion
    return new Promise((resolve, reject) => {
      this.resolvesOfGetFirmwareVersion.push(resolve)
      if (!specialCheckCurrent(speciaBehiver.DevicesOfWitmod)) {
        setTimeout(() => {
          reject('get firmware version timeout')
        }, 1000)
      }
    })
  }

  // 判断当前的固件版本是否大于等于指定版本
  async fwVersionGreaterThanOrEqual(version: string, devices: DevicePid[]) {
    const fwVersion = await this.getFirmwareVersion()
    if (!devices.includes(DeviceManager.instance.state.currentPid)) {
      return false
    }
    return compare(fwVersion, version, '>=')
  }

  // 判断当前的固件版本是否大于等于指定版本A并且小于版本B
  async fwVersionBetween(versionA: string, versionB: string, devices: DevicePid[]) {
    const fwVersion = await this.getFirmwareVersion()
    if (!devices.includes(DeviceManager.instance.state.currentPid)) {
      return false
    }
    return compare(fwVersion, versionA, '>=') && compare(fwVersion, versionB, '<')
  }
  // 判断当前的固件版本是否小于指定版本
  async fwVersionLessThan(version: string, devices: DevicePid[]) {
    const fwVersion = await this.getFirmwareVersion()
    if (!devices.includes(DeviceManager.instance.state.currentPid)) {
      return false
    }
    return compare(fwVersion, version, '<')
  }

  private async isSupportUpgrade(): Promise<boolean> {
    // TODO: 只有在驱动的显示语言为中文简体的情况下, 才会推送升级提示
    if (i18n.global.locale !== 'zh') return false
    const pid = DeviceManager.instance.state.currentPid
    const res = this.updates.filter((e) => e.DeviceIndex == pid)
    if (res.length == 0) {
      return false
    }
    const fwVersion = await this.getFirmwareVersion()
    return compare(res[0].VersionCode, fwVersion, '>')
  }

  // 获取固件版本号，并检查固件是否需要升级
  async checkFirmwareUpdate() {
    await this.askFirmwareVersion()
    await this.fetchLatestFirmwareVersion()
    this.isUpgrade.value = await this.isSupportUpgrade()
  }

  async askFirmwareVersion() {
    if (specialCheck(speciaBehiver.DevicesOfWitmod, DeviceManager.instance.state.currentPid)) {
      await delay(1000)
    }
    try {
      const version = await DriverService.instance.askDeviceVersion()
      this._firmwareVersion = version
      this.firmwareVersion.value = version
      this.resolvesOfGetFirmwareVersion.forEach((resolve) => resolve(version))
    } catch (error) {
      enableDialog(i18n.global.t('deviceVersionGettingErrorAndRetry'), DialogType.notice, false, 6000)
      setTimeout(() => {
        window.location.reload()
      }, 5000)
    }
  }
  async fetchLatestFirmwareVersion() {
    let host = ''
    const bd = await getBrand()
    if (bd == Brand.irok) {
      host = 'https://irok.ast.joyway.net/'
    } else if (bd == Brand.iyx) {
      host = 'https://ncus.iyx.fun/'
    } else if (bd == Brand.carotmas) {
      host = 'https://ncus.carotmas.com/'
    }
    this.updates = await Http.get<UpdateInfo[]>('firmwareVersion/latest', {}, host)
  }

  clear() {
    this.isUpgrade.value = false
    this._firmwareVersion = null
    this.firmwareVersion.value = null
  }

  /**
   * 睿联微固件判断
   * 此版本更新内容：单按键死区设置，KME设置加模式，灯光调整，增加RS
   * @returns true:已完成升级，false:没有完成升级
   */
  versionIs20241125a = async () => {
    const fwGTE107 = await firmware.fwVersionGreaterThanOrEqual('1.0.7', [DevicePid.IROKNa87Pro, DevicePid.IYXMu68Pro, DevicePid.IROKNd63])
    const fwGTE113 = await firmware.fwVersionGreaterThanOrEqual('1.1.3', [DevicePid.IROKNd63_cyan, DevicePid.IYXMu68Pro_cyan])
    const fwGTE102 = await firmware.fwVersionGreaterThanOrEqual('1.0.2', [DevicePid.IYXMu68_ULTRA]) // IYXMu68_ULTRA 102版本未支持单按键死区设置
    return fwGTE107 || fwGTE113 || fwGTE102
  }

  /**
   * 睿联微固件判断
   * 此版本更新内容：高级改键（宏、MT、TGL、KME、DKS）传输数据发生变化，最小行程为0.005，增加3个响应灯效
   * @returns true:已完成升级，false:没有完成升级
   */
  versionIs20250326 = async () => {
    const fwGTE100 = await firmware.fwVersionGreaterThanOrEqual('1.0.1', [DevicePid.IYXPolar75]) // 1.0.3大货版本
    const fwGTE007 = await firmware.fwVersionGreaterThanOrEqual('0.0.7', [...JingTaiMg75Devices])
    const fwGTE004 = await firmware.fwVersionGreaterThanOrEqual('0.0.4', [...JingTaiMer68Devices])
    return fwGTE100 || fwGTE007 || fwGTE004
  }
  /**
     * RoYuan 固件更新
     * 
     * V301  Mer68
     * 1、增加回报率调节（1K与8K切换）
     * 
     * V302  MG68 SE
     * 1、增加回报率测试
     * 
     * V304  MG68 MAX
     * 1. 空格键低于 10% 电量 亮红灯，取消充电指示灯
     * 2. 开放驱动获取充电状态数据
     * 3. 增加回报率调节（1K与8K切换）
     * 4. 修改蓝牙睡眠睡眠不下去的问题
     * 
     * V304  Mars68 PRO Wireless
     * 1、空格键低于10%电量亮红灯，取消充电指示灯
     * 2、开放驱动获取充电状态数据 
     * 3、增加回报率调节 (1K与8K切换)
     * 4、更新规格功能

     * V304  Mars68 SE               
     * 1、增加回报率调节（1K与8K切换）

     * V303  Mars68 PRO / Mars68 / MG68 / MG68 PRO  
     * 1、增加回报率调节（1K与8K切换）
     * 
     * V303 Mars68 Wireless          
     * 1、 空格键低于 10% 电量 亮红灯，取消充电指示灯
     * 2、 开放驱动获取充电状态数据
     * 3、 增加回报率调节（1K与8K切换）
     * 4、 更新规格功能
     *    
     * @returns true:已完成升级，false:没有完成升级
    */
  versionRoYuanV304 = async () => {
    const v301 = await firmware.fwVersionGreaterThanOrEqual('301', [...RoYuanMer68Devices])

    const v302 = await firmware.fwVersionGreaterThanOrEqual('302', [DevicePid.IROKMg68SE])

    const v304 = await firmware.fwVersionGreaterThanOrEqual('304', [DevicePid.CarotmasMars68Pro_Wireless, DevicePid.CarotmasMars68Pro_Wireless_24g, DevicePid.CarotmasMars68SE, DevicePid.IROKMg68Max, DevicePid.IROKMg68Max_24g])
    const v303 = await firmware.fwVersionGreaterThanOrEqual('303', [DevicePid.CarotmasMars68Pro, DevicePid.CarotmasMars68_Wireless, DevicePid.CarotmasMars68_Wireless_24g, DevicePid.CarotmasMars68, DevicePid.IROKMg68, DevicePid.IROKMg68Pro])
    const v502 = await firmware.fwVersionGreaterThanOrEqual('0', RoYuanDeviceList_0005)
    return v301 || v302 || v303 || v304 || v502
  }

  // 固件支持RS的固件版本
  versionRoYuanV307 = async () => {
    if (![DevicePid.IROKMg68Max, DevicePid.IROKMg68Max_24g].includes(DeviceManager.instance.state.currentPid)) {
      return false
    }
    return await firmware.fwVersionGreaterThanOrEqual('307', [DevicePid.IROKMg68Max, DevicePid.IROKMg68Max_24g])
  }

  jingtaiV2_isSupportRS = async () => {
    const rs1 = await firmware.fwVersionGreaterThanOrEqual('1.0.7.1', [DevicePid.IROKMg68Plus])
    const rs2 = await firmware.fwVersionGreaterThanOrEqual('1.0.6.0', [DevicePid.IROKMg75Max])
    const rs4 = await firmware.fwVersionGreaterThanOrEqual('1.0.2.0', [DevicePid.CarotmasMars75Pro])
    const rs5 = await firmware.fwVersionGreaterThanOrEqual('1.0.1.0', [DevicePid.CarotmasMars75, DevicePid.CarotmasMer68Max, DevicePid.CarotmasMer68SE_JT, DevicePid.IROKND68Pro])
    const rs6 = await firmware.fwVersionGreaterThanOrEqual('1.0.0.0', [DevicePid.IROKND63Ultra, DevicePid.IROKRA68])

    return rs1 || rs2 || rs4 || rs5 || rs6
  }

  // 火柴人方案
  // 支持 音乐律动的固件版本
  isHuoChaiRenV106 = async () => {
    const v106 = await firmware.fwVersionGreaterThanOrEqual('1.06', [DevicePid.IROKMG63, DevicePid.IROKMG75V2C12])
    const v107 = await firmware.fwVersionGreaterThanOrEqual('1.07', [DevicePid.IROKRA68_1])
    const v105 = await firmware.fwVersionGreaterThanOrEqual('1.05', [DevicePid.IROKMG75V2C12])
    return v106 || v107 || v105
  }

  // 支持支持去掉保护默认死区的固件版本
  isHuoChaiRenCancelDefaultDeadzone = async () => {
    const v115 = await firmware.fwVersionGreaterThanOrEqual('1.15', [DevicePid.IROKMG63, DevicePid.IROKRA68_1])
    const v106 = await firmware.fwVersionGreaterThanOrEqual('1.06', [DevicePid.IROKMG75V2C12])
    return v115 || v106
  }

  // 支持顶部/底部死区独立设置的固件版本（v1.20+ 使用 0x53 命令）
  isHuoChaiRenCancelDefaultDeadzoneV120 = async () => {
    const v120 = await firmware.fwVersionGreaterThanOrEqual('1.20', [DevicePid.IROKMG63, DevicePid.IROKRA68_1])
    const v120mg75 = await firmware.fwVersionGreaterThanOrEqual('1.20', [DevicePid.IROKMG75V2C12])
    return v120 || v120mg75
  }
}
const firmware = new Firmware()
export default firmware
